import { supertypeClass, property, amorphicStatic } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import _ from 'lodash';
import { FamilyHistory } from './FamilyHistory';
import { Customer } from './Customer';
import { FlexibleObj } from '../interfaces/FlexibleObj';
import { Utils } from '../Utils';
import { FamilyCondition as GQLFamilyCondition } from '@hit/hit-api-client';

@supertypeClass({ toClient: true, toServer: true })
export class FamilyCondition extends Everything implements GQLFamilyCondition {
  @property({ getType: () => FamilyHistory })
  familyHistory: FamilyHistory;

  public static readonly familyMembers = {
    FATHER: 'Father',
    MOTHER: 'Mother',
    BROTHER: 'Brother',
    SISTER: 'Sister',
  };

  @property({
    rule: ['required'],
    values: function () {
      const arr = ['Mother', 'Father'];

      if (this.customer.application.primaryPolicy.insured.familyHistory.brothersLiving !== 'Nobiologicalbrothers') {
        arr.push('Brother');
      }

      if (this.customer.application.primaryPolicy.insured.familyHistory.sistersLiving !== 'Nobiologicalsisters') {
        arr.push('Sister');
      }

      return arr;
    },
    descriptions: { Mother: 'Mother', Father: 'Father', Brother: 'Brother', Sister: 'Sister' },
    comment: 'Biological immediate family member(Mother,Father,Sister,Brother)',
  })
  member: string;

  @property({
    rule: ['required'],
    values: ['isAlive', 'deceased', 'unknown'],
    descriptions: { isAlive: 'Alive and __ years old', deceased: 'Deceased', unknown: 'Unknown' },
    comment: 'Member is still alive or deceased',
  })
  memberLiving: string;

  @property({
    rule: 'numeric',
    validate: 'isWithin(0,110)',
    comment: "Member's age",
  })
  memberAge: number;

  @property({ rule: ['required', 'numeric'], validate: 'isWithin(0, 100)', comment: 'Age member died at' })
  memberDiedAt: number;

  public static readonly conditionModel = {
    base: {
      Cancer: {
        types: {
          'ProstateCancer': {
            description: 'Prostate Cancer',
          },
          'BreastCancer': {
            description: 'Breast Cancer',
          },
          'OvarianCancer': {
            description: 'Ovarian Cancer',
          },
          'Melanoma': {
            description: 'Melanoma',
          },
          'SkinCancer(non-Melanoma)': {
            description: 'Skin Cancer (non-Melanoma)',
          },
          'OtherCancer': {
            description: 'Other Cancer',
          },
          'NotSure': {
            description: 'Not Sure',
          },
        },
        description: 'Cancer',
      },
      HeartDisease: {
        types: {},
        description: 'Heart Disease',
      },
      Diabetes: {
        types: {},
        description: 'Diabetes',
      },
      KidneyDisease: {
        types: {},
        description: 'Kidney Disease',
      },
    },
  };

  // Helper func, returns an object containing all types in this filing's tree model.
  private getCombinedTypes(): FlexibleObj<any> {
    const combinedTypes = {};
    const tree = FamilyCondition.conditionModel.base;

    for (const conditionKey in tree) {
      const typesOfCondition = tree[conditionKey].types;
      for (const typeKey in typesOfCondition) {
        combinedTypes[typeKey] = typesOfCondition[typeKey];
      }
    }

    return combinedTypes;
  }

  @property({
    rule: ['required'],
    values: function (): string[] {
      return Object.keys(FamilyCondition.conditionModel.base);
    },
    descriptions: function (): FlexibleObj<string> {
      const tree = FamilyCondition.conditionModel.base;
      const descs = {};
      for (const conditionKey in tree) {
        descs[conditionKey] = tree[conditionKey].description;
      }

      return descs;
    },
    comment: 'Health condition',
  })
  condition: string;

  // .type stores the subtype of the .condition, if there is one
  @property({
    rule: ['required'],
    values: function (): string[] {
      return Object.keys(this.getCombinedTypes());
    },
    descriptions: function (): FlexibleObj<string> {
      const descs = {};
      const combinedTypes = this.getCombinedTypes();

      for (const typeKey in combinedTypes) {
        descs[typeKey] = combinedTypes[typeKey].description;
      }

      return descs;
    },
    comment: 'Health condition detailed type',
  })
  type: string;

  @property({
    rule: ['required'],
    values: ['onebreast', 'bothbreasts', 'NotSure'],
    descriptions: { onebreast: 'One breast', bothbreasts: 'Both breasts', NotSure: 'Not Sure' },
    comment: "Was your [mother's/sister's/father's/brother's] cancer in one breast or both breasts",
  })
  typeBreastCancerDetails: string;

  @property({
    rule: ['required', 'numeric'],
    validate: 'isWithin(0, 100)',
    comment: 'Age at Onset(breast or ovarian cancer)',
  })
  ageAtOnset: number;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'My [mother/brother/sister/father] died from it.',
  })
  wasCauseOfDeath: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'Unknown'],
    descriptions: { Yes: 'Yes', No: 'No', Unknown: 'Unknown' },
    comment: 'Family Member died from this condition?',
  })
  wasCauseOfDeathStr: string;

  @property({ getType: () => Customer })
  customer: Customer;

  static new(customer) {
    return new FamilyCondition().init(customer);
  }

  init(customer) {
    this.customer = customer;
    return this;
  }

  memberLivingTrigger() {
    if (this.memberLiving !== 'isAlive') {
      this.memberAge = null;
    } else if (this.memberLiving === 'isAlive' || this.memberLiving === 'unknown') {
      this.memberDiedAt = null;
    }
  }
}
